/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

export type Severity = 'low' | 'medium' | 'high' | 'critical';
export type Role = 'user' | 'assistant';
export type AlertTypeId = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface OverallAlert {
  severity: Severity;
  score_10: number; // 0-10
  detected_at: string;
  types_hit: AlertTypeId[];
}

export interface RelatedTurn {
  turn_id: number;
  role: Role;
  text: string;
  redacted: boolean;
  hit_types: AlertTypeId[];
  timestamp: string;
}

export interface Suggestion {
  id: string;
  title: string;
  text: string;
  when_to_use: string;
}

export interface AlertMeta {
  confidence: number; // 0-1
  model_version: string;
  last_updated_at: string;
}

export interface AlertDetailResponse {
  conversation_id: string;
  subject_name: string;
  overall: OverallAlert;
  reasons: string[];
  related_turns: RelatedTurn[];
  suggestions: Suggestion[];
  meta: AlertMeta;
}

// 预警类型枚举定义
export const ALERT_TYPES: Record<AlertTypeId, string> = {
  1: '健康人身安全',
  2: '社交困扰',
  3: '环境不适',
  4: '情绪异常',
  5: '敏感禁忌话题',
  6: '对话内容不适宜',
  7: '家庭与情感问题',
};

// 预警类型说明
export const ALERT_TYPE_DESCRIPTIONS: Record<AlertTypeId, string> = {
  1: '涉及健康风险、人身安全威胁等紧急情况',
  2: '人际关系困扰、社交压力、被孤立等问题',
  3: '环境压力、不适应、周围环境带来的困扰',
  4: '情绪波动剧烈、持续低落、焦虑等异常情绪状态',
  5: '涉及敏感话题、禁忌内容等需要谨慎处理的话题',
  6: '对话内容可能不适宜当前场景或用户',
  7: '家庭矛盾、情感困扰、亲子关系等问题',
};

// 严重程度标签
export const SEVERITY_LABELS: Record<Severity, string> = {
  low: '低风险',
  medium: '中风险',
  high: '高风险',
  critical: '紧急',
};

// 严重程度颜色
export const SEVERITY_COLORS: Record<Severity, string> = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#ff5722',
  critical: '#d32f2f',
};

// 角色标签
export const ROLE_LABELS: Record<Role, string> = {
  user: '用户',
  assistant: 'AI',
};
